import React, { useEffect, useState } from 'react';
import CategoryCard, { Category } from 'src/components/BeaconV2Components/CategoryCard/CategoryCard';
import { useAppSelector } from 'src/utils/Hooks';
import { useStacklineTheme } from '@stackline/ui';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import { calculatePercentChange } from 'src/utils/app';
import SegmentNoDataPlaceholder from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentNoDataPlaceholder';
import { SearchTextField } from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';
import TimePeriodDropdown, {
  getAtlasBeaconTimePeriods
} from 'src/components/BeaconV2Components/common/TimePeriodDropdown';
import ComparisonPeriodDropdown from 'src/components/BeaconV2Components/common/ComparisonPeriodDropdown';
import { Box } from '@mui/material';
import { shouldHidePagination } from 'src/components/BeaconV2Components/EntityTableRefresh/utils';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useAtlasV2TableData } from 'src/serverProxy/useAtlasV2TableData';
import { useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import colors from 'src/utils/colors';

const FETCH_CHUNK_SIZE = 150;
const GRID_ITEMS_PER_PAGE = 20;
/**
 * Category Page Layout that can be access from main Navigation (top nav)
 *
 */

const AtlasV2CategorySummary = () => {
  const theme = useStacklineTheme();
  const { startWeek: compareStartWeek, endWeek: compareEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const retailerId = useRetailerId();
  const categories = useAppSelector((state) => state.categoriesByRetailerId[retailerId]);

  const [initialCategories, setInitialCategories] = useState([
    {
      categoryName: 'All Categories',
      displayName: 'All Categories',
      name: 'All Categories',
      id: 0,
      categoryId: 0
    },
    ...categories
  ]);
  const categoryIds = categories.map((cat) => cat.id);
  const [searchInput, setSearchInput] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(GRID_ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [isViewAll, setIsViewAll] = useState(false);
  const { isLoading: metricDataLoading, data: categoryData } = useAtlasV2TableData({
    fields: [{ name: 'retailSales', canBeExported: false }],
    groupByFieldName: 'categoryId',
    indexName: 'sales',
    requestId: 'categoryRetailSales',
    itemsPerPage: FETCH_CHUNK_SIZE,
    pageNumber: 1,
    sortFieldName: 'retailSales',
    buildRequest: (builder) =>
      builder
        .replaceConditionTermFilter('categoryId', 'should', categoryIds)
        .removeConditionTermFilters((field) => field.fieldName === 'brandId')
  });

  useEffect(() => {
    if (categoryData && !metricDataLoading && categories.length > 0) {
      const categoryRetailSalesMap = categoryData.reduce(
        (map, datum) => {
          const { categoryId } = datum.metadata;
          const currentValue = datum.retailSales_sum_value;
          const changeAmount = datum[`retailSales_sum_value_${compareStartWeek}_${compareEndWeek}`];
          const percentageChange = calculatePercentChange(currentValue, changeAmount);
          // all category values
          map.allCategoryRetailSalesSum += currentValue;
          map.allCategoryRetailSalesChangeAmount += changeAmount;

          return {
            ...map,
            [categoryId]: [datum.retailSales_sum_value, percentageChange]
          };
        },
        {
          allCategoryRetailSalesSum: 0,
          allCategoryRetailSalesChangeAmount: 0,
          0: [0, 0]
        }
      );

      const advancedCategories = [
        { categoryName: 'All Categories', id: 0, categoryId: 0, type: 'category' },
        ...categories
      ]
        .filter((category) => categoryRetailSalesMap[category.id] || category.id === 0)
        .map((category) => {
          const [currentRetailSales, retailSalesChange] = categoryRetailSalesMap[category.id];
          return { ...category, categoryType: category.type, currentRetailSales, retailSalesChange };
        });

      setInitialCategories([
        {
          categoryName: 'All Categories',
          displayName: 'All Categories',
          name: 'All Categories',
          id: 0,
          categoryId: 0,
          currentRetailSales: categoryRetailSalesMap.allCategoryRetailSalesSum,
          retailSalesChange: calculatePercentChange(
            categoryRetailSalesMap.allCategoryRetailSalesSum,
            categoryRetailSalesMap.allCategoryRetailSalesChangeAmount
          )
        },
        ...advancedCategories
      ]);
    }
  }, [categories, categoryData, compareEndWeek, compareStartWeek, metricDataLoading]);

  const filteredCategories = initialCategories.filter((category) => {
    // "All categories" won't have its name
    return (
      (category.name && category.name.toLowerCase().includes(searchInput.toLowerCase())) ||
      category?.childEntities?.some((subCategory) => subCategory.name.toLowerCase().includes(searchInput.toLowerCase()))
    );
  });

  // prevent showing 0 when there is no search list. the total pagination number should still show 1
  const totalPage = Math.ceil(filteredCategories.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const categoriesToDisplay = filteredCategories.slice(startIndex, endIndex);
  const hidePagination = shouldHidePagination({
    page: currentPage,
    pageSize: GRID_ITEMS_PER_PAGE,
    rowCount: (categoriesToDisplay || []).length
  });

  // Handle next page and previous page clicks
  const handleNextPage = () => {
    if (currentPage < totalPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleViewAll = () => {
    setIsViewAll(true);
    setItemsPerPage(initialCategories.length);
    setCurrentPage(1);
  };

  return (
    <>
      <BasePageContainer sx={{ width: '100%', marginTop: '0px' }}>
        <div style={{ width: '100%', position: 'relative' }}>
          <Box display="flex" justifyContent="space-between" width="100%">
            <Text variant="h2">Categories</Text>
            <Flex>
              <Flex paddingX={theme.spacing.md}>
                <TimePeriodDropdown allowedTimePeriods={getAtlasBeaconTimePeriods} />
              </Flex>
              <ComparisonPeriodDropdown />
            </Flex>
          </Box>

          <Flex paddingY={theme.spacing.lg}>
            <SearchTextField
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search"
            />
          </Flex>
          {categoriesToDisplay.length === 0 ? (
            <Flex justifyContent="center" marginTop={theme.spacing.lg}>
              <SegmentNoDataPlaceholder
                title="No search results found"
                subtitle="There is no data available for the search term."
                maxWidth={238}
              />
            </Flex>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' }}>
              {categoriesToDisplay.map((category) => {
                return (
                  <CategoryCard
                    key={category.id}
                    val={category as Category}
                    isLoading={metricDataLoading}
                    boxBackgroundColor={category.id ? null : colors.graysPrimary}
                    boxTextProps={category.id ? {} : { color: 'primary' }}
                  />
                );
              })}
            </div>
          )}
          {isViewAll || hidePagination ? null : (
            <div style={{ marginTop: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  zIndex: 1,
                  position: 'relative'
                }}
              >
                {/* placeholder for the space between */}
                <div>
                  {searchInput.length === 0 && !isViewAll && (
                    <div role="button" onClick={handleViewAll}>
                      <Text variant="subtitle3">View All Categories ({categories.length - 1})</Text>
                    </div>
                  )}
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPage}
                  onClickNext={handleNextPage}
                  onClickPrevious={handlePreviousPage}
                />
              </div>
            </div>
          )}
        </div>
      </BasePageContainer>
    </>
  );
};

export default AtlasV2CategorySummary;
